import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Site1Template } from "../../templates";
import { buildSiteData } from "../../templates/Site1";
import { SITE_SECTIONS, isFromHost, postToHost } from "../../utils/sitePreview";

const LANGUAGES = ["ro", "en"];

/**
 * Scroll this frame, and only this frame, to `target` — or to the top.
 *
 * Not `scrollIntoView`: the preview is a same-origin iframe, so that scrolls
 * every page around it as well, and each section picked in Site & widget
 * dragged the dashboard down to the preview. The offset is the one
 * scrollIntoView would land on: the section's scroll-margin plus the page's
 * scroll-padding, which keep it clear of the sticky header.
 */
const scrollFrameTo = (target, behavior) => {
  const inset = (element, property) => parseFloat(getComputedStyle(element)[property]) || 0;
  const top = target
    ? target.getBoundingClientRect().top +
      window.scrollY -
      inset(target, "scrollMarginTop") -
      inset(document.documentElement, "scrollPaddingTop")
    : 0;
  window.scrollTo({ top, behavior });
};

const scrollToSection = (section, block = null, behavior = "smooth") => {
  const target =
    section === "header" || section === "brand"
      ? null
      : (section === "about" && block !== null && document.querySelector(`[data-about-block="${block}"]`)) ||
        document.querySelector(`[data-edit-section="${section}"]`);
  scrollFrameTo(target, behavior);
};

/**
 * The inside of the preview iframe — see utils/sitePreview.js.
 *
 * Renders nothing until the dashboard sends the settings, then the real Site1
 * page drawn from them by the same `buildSiteData` the public page uses. When
 * the dashboard is editing (`editable`), each section is clickable and reports
 * itself back, and the section being edited is scrolled into view.
 */
const SitePreviewFrame = () => {
  const { t, i18n } = useTranslation();
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const onMessage = (event) => {
      if (!isFromHost(event) || event.data.type !== "render") return;
      setMessage(event.data);
    };
    window.addEventListener("message", onMessage);
    // Sent after the listener is in place, and again on every reload of the
    // frame: the dashboard answers each "ready" with the current draft.
    postToHost("ready");
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const lang = LANGUAGES.includes(message?.lang) ? message.lang : null;
  useEffect(() => {
    if (lang && i18n?.language !== lang) i18n?.changeLanguage(lang);
  }, [lang, i18n]);

  const section = SITE_SECTIONS.includes(message?.section) ? message.section : null;
  const editable = Boolean(message?.editable);
  const block = Number.isInteger(message?.block) && message.block >= 0 ? message.block : null;
  useEffect(() => {
    if (editable && section) scrollToSection(section, block);
  }, [editable, section, block]);

  const editor = useMemo(
    () =>
      editable
        ? {
            active: section,
            activeBlock: block,
            onSelect: (next, index = null) =>
              postToHost("select", { section: next, ...(Number.isInteger(index) ? { block: index } : {}) }),
            labels: Object.fromEntries(
              SITE_SECTIONS.map((id) => [id, t(`Dashboard.MySite.sections.${id}`)]),
            ),
            placeholders: {
              about: t("Dashboard.MySite.preview.emptyAbout"),
              contact: t("Dashboard.MySite.preview.emptyContact"),
            },
          }
        : null,
    [editable, section, block, t],
  );

  if (!message) return null;

  const data = buildSiteData(message.settings, {
    t,
    showsBadge: Boolean(message.showsBadge),
  });

  return (
    <Site1Template
      data={data}
      editor={editor}
      onNavigate={(href) => {
        const target = href.startsWith("#") ? document.getElementById(href.slice(1)) : null;
        scrollFrameTo(target, "smooth");
      }}
    />
  );
};

export default SitePreviewFrame;
